import {
  AppstoreOutlined,
  AuditOutlined,
  BankOutlined,
  DownOutlined,
  LockOutlined,
  LogoutOutlined,
  MenuOutlined,
  MonitorOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { apiClient } from "@nimbus-erp/api-client";
import { BrandMark } from "@nimbus-erp/ui";
import { useQueryClient } from "@tanstack/react-query";
import {
  App,
  Avatar,
  Button,
  Drawer,
  Dropdown,
  Form,
  Input,
  Layout,
  Menu,
  Modal,
} from "antd";
import { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAdminAuth } from "../store/authStore";

const { Sider, Header, Content } = Layout;
const navigation = [
  { key: "/tenants", icon: <BankOutlined />, label: "企业中心" },
  { key: "/packages", icon: <AppstoreOutlined />, label: "版本与套餐" },
  { key: "/audit", icon: <AuditOutlined />, label: "操作审计" },
  { key: "/operations", icon: <MonitorOutlined />, label: "运行监控" },
  { key: "/accounts", icon: <UserOutlined />, label: "平台账号" },
];

export function AdminShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const { user, setSession } = useAdminAuth();
  const accountRoleLabel = user?.permissions.includes("PLATFORM_ACCOUNT_MANAGE")
    ? "超级管理员"
    : user?.permissions.includes("PLATFORM_TENANT_MANAGE")
      ? "平台管理员"
      : "平台运营员";
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileForm] = Form.useForm<{
    username: string;
    displayName: string;
    currentPassword: string;
  }>();
  const [passwordForm] = Form.useForm<{
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }>();
  const visibleNavigation = navigation.filter((item) =>
    item.key === "/accounts"
      ? user?.permissions.includes("PLATFORM_ACCOUNT_MANAGE")
      : !["/audit", "/operations"].includes(item.key) ||
        user?.permissions.includes("PLATFORM_AUDIT_READ"),
  );
  const openPage = (path: string) => {
    setMobileNavigationOpen(false);
    navigate(path);
  };
  const logout = async () => {
    try {
      await apiClient.logout();
    } finally {
      queryClient.clear();
      setSession(null);
      navigate("/login", { replace: true });
    }
  };
  const changePassword = async (values: {
    currentPassword: string;
    newPassword: string;
  }) => {
    setPasswordSaving(true);
    try {
      await apiClient.changePlatformPassword(
        values.currentPassword,
        values.newPassword,
      );
      message.success("密码已修改，请使用新密码重新登录");
      setPasswordOpen(false);
      passwordForm.resetFields();
      queryClient.clear();
      setSession(null);
      navigate("/login", { replace: true });
    } catch (error) {
      message.error(error instanceof Error ? error.message : "密码修改失败");
    } finally {
      setPasswordSaving(false);
    }
  };
  const changeProfile = async (values: {
    username: string;
    displayName: string;
    currentPassword: string;
  }) => {
    setProfileSaving(true);
    try {
      await apiClient.changePlatformProfile(
        values.username,
        values.displayName,
        values.currentPassword,
      );
      message.success("账号信息已修改，请使用新账号重新登录");
      setProfileOpen(false);
      profileForm.resetFields();
      queryClient.clear();
      setSession(null);
      navigate("/login", { replace: true });
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : "账号信息修改失败",
      );
    } finally {
      setProfileSaving(false);
    }
  };
  return (
    <Layout className="admin-shell">
      <Sider width={222} className="admin-sider">
        <div className="admin-sider__brand">
          <BrandMark inverse subtitle="SAAS CONSOLE" />
        </div>
        <div className="admin-sider__label">平台运营</div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          onClick={({ key }) => openPage(key)}
          items={visibleNavigation}
        />
        <div className="admin-sider__version">云猫 ERP · 平台管理</div>
      </Sider>
      <Drawer
        title="平台导航"
        placement="left"
        size={260}
        open={mobileNavigationOpen}
        onClose={() => setMobileNavigationOpen(false)}
        closable={{ placement: "end" }}
        className="admin-mobile-drawer"
      >
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          onClick={({ key }) => openPage(key)}
          items={visibleNavigation}
        />
      </Drawer>
      <Layout>
        <Header className="admin-header">
          <div className="admin-header__status">
            <Button
              className="admin-nav-toggle"
              type="text"
              icon={<MenuOutlined />}
              aria-label="打开平台导航"
              onClick={() => setMobileNavigationOpen(true)}
            />
            <span>平台管理中心</span>
          </div>
          <div className="admin-header__right">
            <Dropdown
              menu={{
                items: [
                  { key: "username", label: user?.username, disabled: true },
                  { type: "divider" },
                  {
                    key: "change-profile",
                    icon: <UserOutlined />,
                    label: "修改账号信息",
                    onClick: () => {
                      profileForm.setFieldsValue({
                        username: user?.username ?? "",
                        displayName: user?.displayName ?? "",
                        currentPassword: "",
                      });
                      setProfileOpen(true);
                    },
                  },
                  {
                    key: "change-password",
                    icon: <LockOutlined />,
                    label: "修改登录密码",
                    onClick: () => setPasswordOpen(true),
                  },
                  {
                    key: "logout",
                    icon: <LogoutOutlined />,
                    label: "退出登录",
                    danger: true,
                    onClick: logout,
                  },
                ],
              }}
              trigger={["click"]}
            >
              <button
                type="button"
                className="admin-account"
                aria-label={`打开${user?.displayName ?? accountRoleLabel}的账户菜单`}
              >
                <Avatar icon={<UserOutlined />} />
                <span className="admin-account__copy">
                  <strong>{user?.displayName}</strong>
                  <small>{accountRoleLabel}</small>
                </span>
                <DownOutlined className="admin-account__chevron" />
              </button>
            </Dropdown>
          </div>
        </Header>
        <Content className="admin-content">
          <Outlet />
        </Content>
      </Layout>
      <Modal
        title="修改平台登录密码"
        open={passwordOpen}
        centered
        className="admin-dialog"
        width={460}
        onCancel={() => {
          setPasswordOpen(false);
          passwordForm.resetFields();
        }}
        onOk={() => passwordForm.submit()}
        okText="保存并重新登录"
        okButtonProps={{ loading: passwordSaving }}
        cancelText="取消"
        destroyOnHidden
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={changePassword}
          autoComplete="off"
        >
          <Form.Item
            name="currentPassword"
            label="当前密码"
            rules={[{ required: true, message: "请输入当前密码" }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              {
                required: true,
                min: 10,
                max: 72,
                message: "请输入 10–72 位新密码",
              },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: "请再次输入新密码" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  return value === getFieldValue("newPassword")
                    ? Promise.resolve()
                    : Promise.reject(new Error("两次输入的密码不一致"));
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="修改平台账号信息"
        open={profileOpen}
        centered
        className="admin-dialog"
        width={460}
        onCancel={() => {
          setProfileOpen(false);
          profileForm.resetFields();
        }}
        onOk={() => profileForm.submit()}
        okText="保存并重新登录"
        okButtonProps={{ loading: profileSaving }}
        cancelText="取消"
        destroyOnHidden
      >
        <Form
          form={profileForm}
          layout="vertical"
          onFinish={changeProfile}
          autoComplete="off"
        >
          <Form.Item
            name="username"
            label="登录账号"
            rules={[
              {
                required: true,
                pattern: /^[A-Za-z0-9][A-Za-z0-9_.@-]{2,79}$/,
                message: "请输入 3–80 位字母、数字或 _.@-",
              },
            ]}
          >
            <Input autoComplete="username" maxLength={80} />
          </Form.Item>
          <Form.Item
            name="displayName"
            label="显示名称"
            rules={[
              {
                required: true,
                whitespace: true,
                max: 80,
                message: "请输入显示名称",
              },
            ]}
          >
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item
            name="currentPassword"
            label="当前密码"
            rules={[{ required: true, message: "请输入当前密码确认修改" }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}
